import { useCallback, useState } from 'react'
import { Armchair, Plus } from 'lucide-react'

import { MasterFormModal } from '@/components/masters/MasterFormModal'
import { MasterServerTable } from '@/components/masters/MasterServerTable'
import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import {
  CHAIR_TYPE_OPTIONS,
  createChair,
  fetchChairsList,
  fetchChairsPage,
  updateChair,
} from '@/lib/api/chairs'
import { cn } from '@/lib/utils'

function emptyForm() {
  return {
    name: '',
    type: 'hair',
    capacity: 1,
    is_active: true,
  }
}

/**
 * @param {import('@/types/api').Chair} chair
 */
function chairToForm(chair) {
  return {
    name: chair.name ?? '',
    type: chair.type ?? 'hair',
    capacity: chair.capacity ?? 1,
    is_active: Boolean(chair.is_active),
  }
}

function getFieldError(error, field) {
  return error?.response?.data?.errors?.[field]?.[0] ?? null
}

function formatChairType(type) {
  return CHAIR_TYPE_OPTIONS.find((option) => option.value === type)?.label ?? type
}

const EXPORT_COLUMNS = [
  { key: 'name', label: 'Name', getValue: (row) => row.name },
  { key: 'type', label: 'Type', getValue: (row) => row.type },
  { key: 'capacity', label: 'Capacity', getValue: (row) => row.capacity },
  { key: 'is_active', label: 'Active', getValue: (row) => (row.is_active ? 'yes' : 'no') },
]

export function ChairsPage() {
  const { activeBranchId } = useBranch()
  const [modalOpen, setModalOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState(emptyForm())
  const [reloadKey, setReloadKey] = useState(0)

  const fetchPage = useCallback((params) => fetchChairsPage(params), [])

  function openCreate() {
    setCreating(true)
    setSelectedId(null)
    setForm(emptyForm())
    setError('')
    setModalOpen(true)
  }

  /**
   * @param {import('@/types/api').Chair} chair
   */
  function openEdit(chair) {
    setCreating(false)
    setSelectedId(chair.id)
    setForm(chairToForm(chair))
    setError('')
    setModalOpen(true)
  }

  function closeModal() {
    if (submitting) return
    setModalOpen(false)
    setError('')
  }

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')

    const payload = {
      ...form,
      capacity: Number(form.capacity) || 1,
    }

    try {
      if (creating) {
        await createChair(payload)
      } else if (selectedId) {
        await updateChair(selectedId, payload)
      }
      setReloadKey((current) => current + 1)
      setModalOpen(false)
    } catch (err) {
      const message =
        getFieldError(err, 'name') ??
        getFieldError(err, 'type') ??
        getFieldError(err, 'capacity') ??
        err?.response?.data?.message ??
        'Unable to save chair.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-8 lg:px-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Armchair className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Chairs & rooms</h1>
            <p className="text-sm text-muted-foreground">
              Treatment stations for scheduling — hair, nail, and spa capacity.
            </p>
          </div>
        </div>
        <Button type="button" variant="outline" onClick={openCreate}>
          <Plus className="h-4 w-4" />
          New chair
        </Button>
      </div>

      <MasterServerTable
        key={activeBranchId}
        fetchPage={fetchPage}
        reloadKey={reloadKey}
        getRowKey={(chair) => chair.id}
        onEdit={openEdit}
        emptyLabel="No chairs yet."
        searchPlaceholder="Search chairs…"
        exportColumns={EXPORT_COLUMNS}
        exportFilename="chairs.csv"
        fetchAllForExport={(params) => fetchChairsList(params)}
        columns={[
          { key: 'name', label: 'Name', sortable: true, render: (row) => row.name },
          {
            key: 'type',
            label: 'Type',
            sortable: true,
            render: (row) => formatChairType(row.type),
          },
          { key: 'capacity', label: 'Capacity', sortable: true, render: (row) => row.capacity },
          {
            key: 'is_active',
            label: 'Status',
            render: (row) => (row.is_active ? 'Active' : 'Inactive'),
          },
        ]}
      />

      <MasterFormModal
        open={modalOpen}
        onClose={closeModal}
        title={creating ? 'New chair' : 'Edit chair'}
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="chair-name" className="mb-1.5 block text-sm font-medium">
              Name
            </label>
            <input
              id="chair-name"
              type="text"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              className={cn(
                'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-ring',
              )}
              placeholder="Chair 1, Spa Room A…"
              required
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="chair-type" className="mb-1.5 block text-sm font-medium">
                Type
              </label>
              <select
                id="chair-type"
                value={form.type}
                onChange={(e) => updateField('type', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
                required
              >
                {CHAIR_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="chair-capacity" className="mb-1.5 block text-sm font-medium">
                Capacity
              </label>
              <input
                id="chair-capacity"
                type="number"
                min={1}
                max={255}
                value={form.capacity}
                onChange={(e) => updateField('capacity', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
                required
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => updateField('is_active', e.target.checked)}
              className="rounded border-border"
            />
            Chair is active
          </label>

          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={closeModal} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : creating ? 'Create chair' : 'Save chair'}
            </Button>
          </div>
        </form>
      </MasterFormModal>
    </div>
  )
}
